/**
 * Custom Exercise Service
 *
 * Service for managing custom exercise operations including CRUD operations,
 * exercise sharing, and filtering. Extends the base
 * CrudService to provide exercise-specific functionality with proper
 * validation and error handling.
 *
 * @module services/custom-exercise
 */

import { ZodError } from 'zod';

import {
  InvalidDataError,
  invalidDataErrorFromZod,
} from '../errors/invalid-data.error';
import { apiClient } from './api/client';
import { rethrowTyped } from './api/errors';
import { CrudService } from './crud.service';
import { selectizeService } from './selectize.service';
import {
  validateCustomExercise,
  type CustomExerciseInput,
} from '../validation/custom-exercise.schemas';

import type { QueryParams } from '../types/api/query.types';
import type { ID } from '../types/common/id.types';
import type { CustomExercise } from '../types/domain/exercise.types';

// =============================================================================
// THIRD PARTY EXERCISE SERVICE CLASS
// =============================================================================

/**
 * CustomExerciseService - Handles custom exercise database operations
 * Manages exercises from third-party exercise databases with full CRUD capabilities
 */
export class CustomExerciseService extends CrudService<
  CustomExercise,
  CustomExercise,
  CustomExerciseInput
> {
  constructor(cacheTTL?: number) {
    super({
      baseUrl: '/custom-exercise',
      enableCaching: true,
      cacheTTL: cacheTTL || 5 * 60 * 1000, // 5 minutes default
    });
  }

  /**
   * Search custom exercises with advanced filtering
   *
   * @param params - Query parameters for filtering exercises
   * @returns Promise resolving to custom exercises list
   */
  public async getExercises(params: QueryParams): Promise<CustomExercise[]> {
    try {
      return await this.getAll(params);
    } catch (error) {
      console.error('Failed to search custom exercises:', error);
      rethrowTyped(
        error,
        'Unable to search custom exercises. Please try again.'
      );
    }
  }

  /**
   * Get detailed custom exercise information by ID
   *
   * @param id - Custom exercise identifier
   * @returns Promise resolving to custom exercise details
   */
  public async getExerciseById(id: ID): Promise<CustomExercise> {
    if (!id) {
      throw new Error('Custom exercise ID is required');
    }

    try {
      const exercise = await this.getById(id);
      return this.transformResponse(exercise);
    } catch (error) {
      console.error(`Failed to fetch custom exercise ${id}:`, error);
      rethrowTyped(
        error,
        'Unable to fetch custom exercise details. Please check the custom exercise ID and try again.'
      );
    }
  }

  /**
   * Create a new custom exercise with validation
   * Validates custom exercise data before sending to API
   *
   * @param data - Custom exercise creation data
   * @returns Promise resolving to created custom exercise
   */
  public async createExercise(
    data: CustomExerciseInput
  ): Promise<CustomExercise> {
    try {
      // Validate custom exercise data
      this.validateCustomExerciseData(data);

      // Transform data before sending to API
      const transformedData = this.transformRequest(data);

      const createdExercise = await this.create(transformedData);
      // A new exercise may introduce body parts / muscles not previously used.
      selectizeService.clearCache();
      return this.transformResponse(createdExercise);
    } catch (error) {
      console.error('Failed to create custom exercise:', error);
      // Preserves a typed ApiErrorClass (incl. any 402) and InvalidDataError;
      // only a genuinely untyped throw gets the fallback message.
      rethrowTyped(
        error,
        'Unable to create custom exercise. Please check your data and try again.'
      );
    }
  }

  /**
   * Update an existing custom exercise
   * Validates updated data and merges with existing custom exercise
   *
   * @param id - Custom exercise identifier
   * @param data - Updated custom exercise data
   * @returns Promise resolving to updated custom exercise
   */
  public async updateExercise(
    id: ID,
    data: CustomExerciseInput
  ): Promise<CustomExercise> {
    if (!id) {
      throw new Error('Custom exercise ID is required');
    }

    try {
      // Validate custom exercise data
      this.validateCustomExerciseData(data);

      // Transform data before sending to API
      const transformedData = this.transformRequest(data);

      const updatedExercise = await this.update(id, transformedData);
      // Editing targeting may add or remove distinct body parts / muscles.
      selectizeService.clearCache();
      return this.transformResponse(updatedExercise);
    } catch (error) {
      console.error(`Failed to update custom exercise ${id}:`, error);
      rethrowTyped(
        error,
        'Unable to update custom exercise. Please check your data and try again.'
      );
    }
  }

  /**
   * Delete a custom exercise by ID
   * Permanently removes the custom exercise and all associated data
   *
   * @param id - Custom exercise identifier
   * @returns Promise resolving when deletion is complete
   */
  public async deleteExercise(id: ID): Promise<void> {
    if (!id) {
      throw new Error('Custom exercise ID is required');
    }

    try {
      await this.delete(id);
      // Deleting may remove the last exercise using a given body part / muscle.
      selectizeService.clearCache();
    } catch (error) {
      console.error(`Failed to delete custom exercise ${id}:`, error);
      rethrowTyped(
        error,
        'Unable to delete custom exercise. Please try again.'
      );
    }
  }

  /**
   * Restore a soft-deleted custom exercise by ID
   * Used to undo a recent deletion before the record is pruned
   *
   * @param id - Custom exercise identifier
   * @returns Promise resolving when the restore completes
   */
  public async restoreExercise(id: ID): Promise<void> {
    if (!id) {
      throw new Error('Custom exercise ID is required');
    }

    try {
      // Custom exercises are addressed by the `exercise` slug on the
      // cross-resource undo-delete route (not this service's baseUrl).
      await this.undoDelete('exercise', id);
      // Restoring may re-introduce a body part / muscle to the selectize options.
      selectizeService.clearCache();
    } catch (error) {
      console.error(`Failed to restore custom exercise ${id}:`, error);
      rethrowTyped(
        error,
        'Unable to restore custom exercise. Please try again.'
      );
    }
  }

  // ---------------------------------------------------------------------------
  // ADVANCED CUSTOM EXERCISE OPERATIONS
  // ---------------------------------------------------------------------------

  /**
   * Copy a custom exercise by ID
   * Copies public custom exercise to user's own collection of custom
   * exercises with the same data as the original
   *
   * @param id - Custom exercise identifier
   * @returns Promise resolving when copy is complete
   */
  public async copyExercise(id: ID): Promise<void> {
    if (!id) {
      throw new Error('Custom exercise ID is required');
    }

    try {
      await apiClient.post(`/custom-exercise/${id}/copy`);
      // The raw POST bypasses the base class's write-path invalidation, so the
      // cached library lists must be dirtied by hand or they keep serving the
      // pre-copy library until the TTL expires.
      this.invalidateListCache();
      // Copying adds a public exercise (and its values) to the user's set.
      selectizeService.clearCache();
    } catch (error) {
      console.error(`Failed to copy custom exercise ${id}:`, error);
      rethrowTyped(error, 'Unable to copy custom exercise. Please try again.');
    }
  }

  // ---------------------------------------------------------------------------
  // CACHE MANAGEMENT
  // ---------------------------------------------------------------------------

  /**
   * Invalidate this service's cached library lists
   * For writers that add to the user's collection outside this service's own
   * CRUD methods (e.g. importing from the external catalog), which the base
   * class invalidation cannot see
   */
  public invalidateListCache(): void {
    this.invalidateCache(['getAll', 'getPaginated', 'search', 'count']);
  }

  // ---------------------------------------------------------------------------
  // VALIDATION AND TRANSFORMATION METHODS
  // ---------------------------------------------------------------------------

  /**
   * Validate custom exercise data using Zod schema
   * Ensures data meets requirements before API calls
   *
   * @param data - Custom exercise data to validate
   * @throws Error if validation fails
   */
  protected validateCustomExerciseData(data: CustomExerciseInput): void {
    try {
      validateCustomExercise(data);
    } catch (error) {
      if (error instanceof ZodError) {
        throw invalidDataErrorFromZod("Couldn't save the exercise", error);
      }
      throw new InvalidDataError(
        "Couldn't save the exercise: the data is in an unexpected format"
      );
    }
  }

  /**
   * Transform request data before sending to API
   * Formats data according to API expectations
   *
   * @param data - Original request data
   * @returns Transformed request data
   */
  protected override transformRequest(
    data: Partial<CustomExerciseInput>
  ): Partial<CustomExerciseInput> {
    // Add any necessary data transformations here
    // For example, converting enum values or formatting dates
    return {
      ...data,
    };
  }

  /**
   * Transform response data received from API
   * Formats data for frontend consumption
   *
   * @param data - Raw API response data
   * @returns Transformed response data
   */
  protected override transformResponse(data: CustomExercise): CustomExercise {
    // Add any necessary response transformations here
    // For example, parsing dates or converting values
    return {
      ...data,
    };
  }
}

// =============================================================================
// SERVICE INSTANCE
// =============================================================================

/**
 * Default workout service instance
 * Pre-configured and ready to use throughout the application
 */
export const customExerciseService = new CustomExerciseService();
